import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, IRowNode } from 'ag-grid-community';
import { AsyncConfirmationDialogComponent } from '../../../../core/components/async-confirmation-dialog/async-confirmation-dialog.component';
import { LinkButtonComponent } from '../../../../core/components/buttons/link/link-button.component';
import { LoadingButtonComponent } from '../../../../core/components/buttons/loading/loading-button.component';
import { FormErrorsComponent } from '../../../../core/components/form-errors/form-errors.component';
import { InputFechaComponent } from '../../../../core/components/inputs/input-fecha/input-fecha.component';
import { InputNumberComponent } from '../../../../core/components/inputs/input-number/input-number.component';
import { InputTextareaComponent } from '../../../../core/components/inputs/input-textarea/input-textarea.component';
import { AG_GRID_DEFAULT_OPTIONS, removeRowButton } from '../../../../core/config/aggrid.default.config';
import { BackendErrorService } from '../../../../core/services/backend-error/backend-error.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { ReplaceEmptyStringsWithNull } from '../../../../core/utils/ReplaceEmptyStringsWithNull';
import { EmpresaFacturadoraService } from '../../../../maestros/services/maestros/empresa-facturadora/empresa-facturadora.service';
import { EmpresaFacturadora } from '../../../../models/maestros/empresa-facturadora.model';
import { GastoDtoIn, GastoDtoOut, GastoForm } from '../../../models/gasto.model';
import { ContabilidadGastoService } from '../../../services/contabilidad-gasto.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-gasto-detail',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextareaComponent,
    MatIconModule,
    LoadingButtonComponent,
    RouterModule,
    InputFechaComponent,
    InputNumberComponent,
    AsyncConfirmationDialogComponent,
    AgGridAngular,
    FormErrorsComponent,
    FormsModule,
    LoadingButtonComponent,
  ],
  templateUrl: './gasto-detail.component.html',
  styleUrl: './gasto-detail.component.scss',
})
export class GastoDetailComponent implements OnInit {
  gastoId!: number;
  gastoForm!: ReturnType<typeof this.fb.group<GastoForm>>;
  isEditMode = false;
  isLoading = false;
  gastoData!: GastoDtoOut;
  gridApi!: GridApi;

  mostrarDialogoValidacion = false;
  mostrarDialogoCambiarCodigoAlbaran = false;
  isLoadingValidar = false;

  mostrarDiaologoEliminar = false;
  isLoadingEliminar = false;

  empresasRefacturadoras: EmpresaFacturadora[] = [];

  nuevoCodigoAlbaran: number = 0;
  isLoadingCambiarCodigoAlbaran = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly service: ContabilidadGastoService,
    private readonly toastService: ToastService,
    private readonly backendErrorService: BackendErrorService,
    private readonly router: Router,
    private readonly empresaFacturadoraService: EmpresaFacturadoraService
  ) {}

  gridOptions: GridOptions = {
    ...AG_GRID_DEFAULT_OPTIONS,
    onGridReady: (params) => (this.gridApi = params.api),
  };

  get columnDefs(): ColDef[] {
    const baseColumns: ColDef[] = [
      { field: 'id', headerName: 'ID' },
      { field: 'codFactura', headerName: 'Código Factura' },
      { field: 'codAlbaran', headerName: 'Código Albarán' },
      { field: 'nombreProveedor', headerName: 'Proveedor' },
      { field: 'fechaEmision', headerName: 'Fecha Emisión', type: 'dateColumn' },
      { field: 'baseImponible', headerName: 'Base Imponible', type: 'numberColumn' },
      { field: 'importeTotal', headerName: 'Importe Total', type: 'numberColumn' },
      { field: 'numeroFacturaCompra', headerName: 'Número Factura Compra' },
      { field: 'fechaFacturaCompra', headerName: 'Fecha Factura Compra', type: 'dateColumn' },
      {
        field: '',
        headerName: 'Ver Factura',
        cellRenderer: LinkButtonComponent,
        cellRendererParams(params: any) {
          return {
            link: `/facturas-suministros/${params.data.id}`,
          };
        },
        resizable: false,
        sortable: true,
        filter: false,
        suppressMovable: true,
      },
    ];
    if (this.isEditMode) {
      return [...baseColumns, removeRowButton(this.afterRemoveRow.bind(this))];
    }
    return baseColumns;
  }

  afterRemoveRow(rowNode: IRowNode): void {
    const idAEliminar = Number(rowNode.id);
    console.log('Eliminando factura con ID:', idAEliminar);
    this.gastoForm
      .get('facturas')
      ?.setValue(this.gastoForm.get('facturas')!.value.filter((id: number) => id !== idAEliminar));
  }

  ngOnInit(): void {
    this.gastoId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadGasto();
  }

  cargarTablasMaestras(): void {
    this.empresaFacturadoraService.setComboEmpresasFacturadoras(this.empresasRefacturadoras);
  }

  private loadGasto(): void {
    this.service.obtenerGastoPorId(this.gastoId).subscribe({
      next: (data) => {
        this.cargarTablasMaestras();

        this.gastoData = data;
        this.initForm(data);
      },
    });
  }

  private initForm(data: GastoDtoOut): void {
    this.gastoForm = this.fb.group<GastoForm>({
      empresaFacturadoraId: this.fb.control(data.empresaFacturadoraId ?? null, { nonNullable: true }),
      fechaAsientoContable: this.fb.control(data.fechaAsientoContable ?? null, { nonNullable: true }),
      codAlbaranInicial: this.fb.control(data.codAlbaranInicial ?? null),
      fechaValidacion: this.fb.control(data.fechaValidacion ?? null),
      observaciones: this.fb.control(data.observaciones ?? null),
      facturas: this.fb.control(
        (data.facturas ?? []).map((f) => f.id),
        { nonNullable: true }
      ),
    });

    this.gastoForm.disable();
    this.nuevoCodigoAlbaran = data.codAlbaranInicial ?? 0;
  }

  toggleEdit(): void {
    this.isEditMode = !this.isEditMode;
    this.isEditMode ? this.gastoForm.enable() : this.gastoForm.disable();

    this.gastoForm.get('empresaFacturadoraId')?.disable();
    this.gastoForm.get('fechaAsientoContable')?.disable();
    this.gastoForm.get('fechaValidacion')?.disable();
    this.gastoForm.get('codAlbaranInicial')?.disable();
  }

  guardarCambios(): void {
    const raw = this.gastoForm.getRawValue();
    const cleaned: GastoDtoIn = ReplaceEmptyStringsWithNull.clean(raw);
    this.isLoading = true;

    this.service.actualizarGasto(cleaned, this.gastoId).subscribe({
      next: () => {
        this.toastService.success('Gasto actualizado');
        this.isEditMode = false;
        this.gastoForm.get('fechaAsientoContable')?.setValue(new Date().toISOString().slice(0, 10));
        this.gastoForm.disable();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        if (err?.error?.errors) {
          this.backendErrorService.handleBackendErrors(err.error.errors, this.gastoForm);
        } else {
          this.toastService.error('Error al actualizar el gasto');
        }
      },
    });
  }

  exportarExcel(): void {
    this.service.exportarGastoExcel(this.gastoId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gasto_${this.gastoData.id}_${this.gastoData.fechaAsientoContable}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar el gasto');
      },
    });
  }

  exportarCsv(): void {
    this.service.exportarGastoCsv(this.gastoId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gasto_${this.gastoData.id}_${this.gastoData.fechaAsientoContable}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.error('Error al exportar el gasto');
      },
    });
  }

  cancelarEdicion(): void {
    this.initForm(this.gastoData);
    this.isEditMode = false;
  }

  validarGasto(): void {
    this.isLoadingValidar = true;

    this.service.validarGasto(this.gastoId).subscribe({
      next: () => {
        this.toastService.success('Gasto validado correctamente');
        this.gastoData.validado = true;
        this.gastoData.fechaValidacion = new Date().toISOString().slice(0, 10);
        this.gastoForm.get('fechaValidacion')?.setValue(this.gastoData.fechaValidacion);
        this.gastoForm.disable();
      },
      error: (err) => {
        this.toastService.error('Error al validar el gasto');
        console.error('Error al validar el gasto:', err);
        this.isLoadingValidar = false;
        this.mostrarDialogoValidacion = false;
      },
      complete: () => {
        this.isLoadingValidar = false;
        this.mostrarDialogoValidacion = false;
      },
    });
  }

  eliminarGasto(): void {
    this.isLoadingEliminar = true;

    this.service
      .eliminarGasto(this.gastoId)
      .pipe(
        finalize(() => {
          this.isLoadingEliminar = false;
          this.mostrarDiaologoEliminar = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Gasto eliminado correctamente');
          this.router.navigate(['/contabilidad/gastos']);
        },
        error: (err) => {
          this.toastService.error('Error al eliminar el gasto');
          console.error('Error al eliminar el gasto:', err);
        },
      });
  }

  cambiarCodigoAlbaran(): void {
    this.isLoadingCambiarCodigoAlbaran = true;

    this.service
      .cambiarCodigoAlbaran(this.gastoId, this.nuevoCodigoAlbaran)
      .pipe(
        finalize(() => {
          this.isLoadingCambiarCodigoAlbaran = false;
          this.cerrarModal('modalCambiarCodigoAlbaran');
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success('Código de albarán cambiado correctamente');
          this.loadGasto();
        },
        error: (err) => {
          this.toastService.error('Error al cambiar el código de albarán');
          console.error('Error al cambiar el código de albarán:', err);
        },
      });
  }

  abrirModal(modalSeleccionado: string) {
    const modal = document.getElementById(modalSeleccionado) as HTMLDialogElement;
    if (modal) {
      modal.showModal();
    }
  }

  cerrarModal(modalSeleccionado: string) {
    const modal = document.getElementById(modalSeleccionado) as HTMLDialogElement;
    if (modal) {
      modal.close();
    }
  }
}
